












































































































































































































































































import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import {VueMaskDirective} from 'v-mask';
import BindAttrs from '@/utils/binds-attrs';
import AmetrasSimpleTextOutputTemplate from '@/fields/AmetrasSimpleTextOutputTemplate.vue';
import {isEqual} from 'lodash';
import {HTML_SERVICE_INSTANCE} from '@/services/html.service';
import moment from 'moment';
import '@/compiled-icons/chevron-right';
import '@/compiled-icons/chevron-left';
import {ResizeSensor} from 'css-element-queries';
import {FieldStyleContext} from '@/fields/field-style-context';

interface DatePreset {
  label: string;
  calculateValue: () => string;
}

/**
 * General Component for Fields of type DATE
 *
 * https://confluence.ametras.com/x/L4RQAw
 *
 * attachRelatively:
 *  - true: attaches the dialog relatively to the component wrapper
 *          This is needed in case of a position:fixed surrounding panel (usually NOT)
 */
@Component({
  components: {AmetrasSimpleTextOutputTemplate},
  directives: {
    VueMaskDirective
  },
  mixins: [BindAttrs]
})
export default class AmetrasDateFieldTemplate extends Vue {
  @Prop() textAlign!: 'LEFT' | 'RIGHT' | 'CENTER' | 'JUSTIFIED';
  @Prop({
    required: false,
    default: 'DD.MM.YYYY'
  }) dateFormat!: string;
  @Prop({
    required: false,
    default: 'YYYY-MM-DD'
  }) isoFormat!: string;
  @Prop({
    required: false
  }) value!: string;
  @Prop({
    required: false,
    default: false
  }) monthPicker!: boolean;
  @Prop({
    required: false,
    default: false
  }) required!: boolean;
  @Prop() marginDefinition!: string;
  @Prop() paddingDefinition!: string;
  @Prop() hideDefaultIcon!: boolean;
  /**
   * This is a flag indicating that the placeholder should not be cleared if readonly mode is active.
   */
  @Prop({
    default: true
  }) clearPlaceholderIfEmpty!: boolean;
  @Prop() readonly!: boolean;
  /**
   * Indicates that we should render a simple mode of this component (increases the rendering performance)
   */
  @Prop() simple!: boolean;
  @Prop({
    default: () => {
      return moment().format('YYYY-M');
    }
  }) currentPickerYearMonth!: string;
  /**
   * Indicates that all errors should be shown
   */
  @Prop() forceShowErrors!: boolean;
  @Prop() typeSpecificSettings!: DisguiseDto.DateFieldSpecificSettings;
  /**
   * any external error present
   */
  @Prop() error!: boolean;
  @Prop() styleContext!: FieldStyleContext;
  showPicker = false;
  localeFormattedTextFieldValue = ''; // depending on the locale, e.g. (de) 12.03.2019
  isoFormattedDatepickerValue = ''; // iso formatted datepicker value, e.g. 2019-05-12
  mask = '';
  listeners: Record<string, any> = {};
  calculatedPastPickers: number = 0;
  calculatedFuturePickers: number = 0;
  showInnerPicker: boolean = false;
  left: boolean = false;
  top: boolean = false;
  datepickerWidth: number = 290;
  presets: DatePreset[] = [{
    label: 'gestern',
    calculateValue: () => {
      return moment().subtract(1, 'days').format(this.isoFormat);
    }
  }, {
    label: 'heute',
    calculateValue: () => {
      return moment().format(this.isoFormat);
    }
  }, {
    label: 'morgen',
    calculateValue: () => {
      return moment().add(1, 'days').format(this.isoFormat);
    }
  }];

  dateRules = [(value: string) => {
    // validates the field value
    if (value && (this.forceShowErrors || !this.readonly)) {
      const isValidDate = this.$moment(value, this.dateFormat, true).isValid();
      if (!isValidDate) {
        return 'ungültige Eingabe (' + this.dateFormat + ')';
      }
    } else if (!value && this.required && (this.forceShowErrors || !this.readonly)) {
      return 'Datum fehlt';
    }
    const notHasAnyExternalError = typeof this.error === 'boolean' ? !this.error : true;
    return notHasAnyExternalError; // if any error from outside or any local error, return false
  }];

  prevUnitLabel: Partial<Record<DisguiseDto.EnumDateUnitDto, string>> = {
    DAY: 'vorheriger Tag',
    WEEK: 'vorherige Woche',
    MONTH: 'vorheriger Monat',
  };
  nextUnitLabel: Partial<Record<DisguiseDto.EnumDateUnitDto, string>> = {
    DAY: 'nächster Tag',
    WEEK: 'nächste Woche',
    MONTH: 'nächster Monat',
  };

  $refs!: {
    inputfield: Vue
  };

  private originalInputListeners: any[] = [];
  private originalChangeListeners: any[] = [];
  private internalCurrentPickerYearMonth: string = '';

  private resizeListener: ResizeSensor | undefined;


  created() {
    this.internalCurrentPickerYearMonth = this.currentPickerYearMonth;
    if (this.value) {
      // if somebody provided a value from outside
      this.updateInternalModel(this.value);
    }
    // based on the current locale
    // this will create a mask, e.g. xx.xx.xxxx (representing masked DD.MM.YYYY)
    this.mask = this.dateFormat.replace(/[mdy]/gi, '#');

    if (this.$listeners.input) {
      if (Array.isArray(this.$listeners.input)) {
        this.originalInputListeners = this.originalInputListeners.concat(this.$listeners.input);
      } else {
        this.originalInputListeners.push(this.$listeners.input);
      }
    }
    if (this.$listeners.change) {
      if (Array.isArray(this.$listeners.change)) {
        this.originalChangeListeners = this.originalChangeListeners.concat(this.$listeners.change);
      } else {
        this.originalChangeListeners.push(this.$listeners.change);
      }
    }
    this.listeners = {
      ...this.$listeners,
      input: (newValue: string) => {
        this.applyModelValueIfValid(newValue, (valueToBePublished) => {
          // update model only if valid.
          for (const fnc of this.originalInputListeners) {
            fnc(valueToBePublished);
          }
        });
      },
      change: (newValue: string) => {
        this.applyModelValueIfValid(newValue, (valueToBePublished) => {
          // update model only if valid.
          for (const fnc of this.originalChangeListeners) {
            fnc(valueToBePublished);
          }
        });
      }
    };
  }

  @Watch('value') modelChanges(newValue: string, oldValue: string) {
    if (!isEqual(newValue, oldValue)) {
      // if somebody provided a new value from outside
      this.updateInternalModel(newValue);
      this.refreshValidation();
    }
  }

  @Watch('required') requiredChanges(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      if (newValue === false) {
        this.refreshValidation();
      }
    }
  }

  @Watch('showPicker') pickerVisible(newValue: string) {
    if (newValue) {
      document.addEventListener('keydown', this.arrowNavigationListener);
      this.resizeListener = new ResizeSensor(document.body, () => {
        this.calculatePickers();
      });
      this.calculatePickers();
      this.$emit('focus');
    } else {
      document.removeEventListener('keydown', this.arrowNavigationListener);
      if (this.resizeListener) {
        this.resizeListener.detach();
      }
      this.$emit('blur');
    }
  }

  @Watch('isoFormattedDatepickerValue') onDatePicked(newValue: string, oldValue: string) {
    if (!isEqual(newValue, oldValue)) {
      // someone picked a value via picker
      const valueAsMoment = this.$moment(newValue, this.isoFormat, true);
      if (valueAsMoment.isValid()) {
        const formattedDate = valueAsMoment.format(this.isoFormat);
        if (!isEqual(formattedDate, this.value)) {
          for (const fnc of (this.originalChangeListeners || []).concat(this.originalInputListeners || [])) {
            fnc(formattedDate);
          }
        }
        this.localeFormattedTextFieldValue = valueAsMoment.format(this.dateFormat);
        Vue.nextTick(() => {
          this.refreshValidation();
        });
      }
    }
  }

  @Watch('error') errorChanges(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      this.refreshValidation();
    }
  }

  afterDatePicked() {
    this.showPicker = false;
    setTimeout(() => {
      // with minimum delay, focus the field again
      HTML_SERVICE_INSTANCE.focusFirstNode(this.$el);
    });
  }

  captureEscKey(event: Event) {
    if (this.showPicker) {
      // prevent blubbling of escape event if datepicker is shown
      event.stopPropagation();
      this.showPicker = false;
    }
  }

  next() {
    this.internalCurrentPickerYearMonth = moment(this.internalCurrentPickerYearMonth, 'YYYY-M').add(1, 'month').format('YYYY-M');
  }

  prev() {
    this.internalCurrentPickerYearMonth = moment(this.internalCurrentPickerYearMonth, 'YYYY-M').subtract(1, 'month').format('YYYY-M');
  }

  nextDate() {
    const unit = (this.typeSpecificSettings || {}).dateIteratorUnit;
    if (unit === undefined || unit === 'DAY') {
      this.updateInternalModel(moment(this.value, this.isoFormat).add('1', 'days').format(this.isoFormat));
    } else if (unit === 'MONTH') {
      this.updateInternalModel(moment(this.value, this.isoFormat).add('1', 'month').format(this.isoFormat));
    } else if (unit === 'WEEK') {
      this.updateInternalModel(moment(this.value, this.isoFormat).add('1', 'week').format(this.isoFormat));
    }
  }

  prevDate() {
    const unit = (this.typeSpecificSettings || {}).dateIteratorUnit;
    if (unit === undefined || unit === 'DAY') {
      this.updateInternalModel(moment(this.value, this.isoFormat).subtract('1', 'days').format(this.isoFormat));
    } else if (unit === 'MONTH') {
      this.updateInternalModel(moment(this.value, this.isoFormat).subtract('1', 'month').format(this.isoFormat));
    } else if (unit === 'WEEK') {
      this.updateInternalModel(moment(this.value, this.isoFormat).subtract('1', 'week').format(this.isoFormat));
    }
  }

  activatePreset(preset: DatePreset) {
    if (!this.readonly) {
      this.updateInternalModel(preset.calculateValue());
    }
  }

  private updateInternalModel(newValue: string) {
    const parsedMomentFromValue = this.$moment(newValue, [this.isoFormat, moment.ISO_8601], true);
    if (parsedMomentFromValue.isValid()) {
      this.localeFormattedTextFieldValue = parsedMomentFromValue.format(this.dateFormat);
      this.isoFormattedDatepickerValue = parsedMomentFromValue.format(this.isoFormat);
      this.internalCurrentPickerYearMonth = parsedMomentFromValue.format('YYYY-M');
    } else {
      this.localeFormattedTextFieldValue = '';
      this.isoFormattedDatepickerValue = '';
      this.internalCurrentPickerYearMonth = moment().format('YYYY-M');
    }
  }

  private applyModelValueIfValid(newValue: string, validCb: (valueToBePublished: string | null) => void) {
    const valueAsMoment = this.$moment(newValue, this.dateFormat, true);
    if (valueAsMoment.isValid()) {
      this.isoFormattedDatepickerValue = valueAsMoment.format(this.isoFormat);
      this.internalCurrentPickerYearMonth = valueAsMoment.format('YYYY-M');
      validCb(valueAsMoment.format(this.isoFormat));
    } else if (newValue === '') {
      this.isoFormattedDatepickerValue = '';
      validCb(newValue);
    }
  }

  private calculatePickers() {
    const inputFieldRect = this.$refs.inputfield.$el.getBoundingClientRect();
    let availableWidth: number;
    if (((inputFieldRect.left - this.$vuetify.application.left) + (inputFieldRect.width / 2)) > ((document.body.clientWidth - this.$vuetify.application.left) / 2)) {
      this.left = true;
      availableWidth = inputFieldRect.left - this.$vuetify.application.left;
    } else {
      this.left = false;
      availableWidth = document.body.clientWidth - inputFieldRect.x;
    }
    if ((inputFieldRect.top + 280 + 16) > document.body.clientHeight) {
      this.top = true;
    } else {
      this.top = false;
    }

    const offset = 14;
    const pastPickerRaw = (this.typeSpecificSettings || {}).pastPickers;
    const futurePickerRaw = (this.typeSpecificSettings || {}).futurePickers;
    let pastPickerCount = pastPickerRaw !== undefined ? pastPickerRaw : 1;
    let futurePickerCount = futurePickerRaw !== undefined ? futurePickerRaw : 1;
    let neededSpace = offset + this.datepickerWidth + (this.datepickerWidth * futurePickerCount) + (this.datepickerWidth * pastPickerCount);
    if (neededSpace > availableWidth) {
      // calculation needed

      // 1. subtract pastPickers
      while (neededSpace > availableWidth && pastPickerCount > 0) {
        pastPickerCount--;
        neededSpace = offset + this.datepickerWidth + (this.datepickerWidth * futurePickerCount) + (this.datepickerWidth * pastPickerCount);
      }
      // 2. subtract futurePickers
      while (neededSpace > availableWidth && futurePickerCount > 0) {
        futurePickerCount--;
        neededSpace = offset + this.datepickerWidth + (this.datepickerWidth * futurePickerCount) + (this.datepickerWidth * pastPickerCount);
      }
    }
    this.calculatedPastPickers = pastPickerCount;
    this.calculatedFuturePickers = futurePickerCount;
  }

  private arrowNavigationListener = (event: KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowLeft': {
        this.prev();
        break;
      }
      case 'ArrowRight': {
        this.next();
        break;
      }
      default:
        break;
    }
  }

  private refreshValidation() {
    if (!this.simple) {
      const inputFieldRef = this.$refs.inputfield as any;
      if (inputFieldRef !== undefined) {
        inputFieldRef.validate();
      } else {
        throw new Error('Cannot refresh validation, the input field is not available');
      }
    }
  }

}
